import {
  IBusinessPlanningCategoryData,
  IBusinessPlanningCategoryItem,
  IBusinessPlanningCategoryProjection,
  IBusinessPlanningProjection,
  IBusinessPlanningYtdData,
  IBrokerageAnniversaryModel,
} from "../types/types";

/**
 * Business Planning Calculator Utility
 * Contains all calculation functions for the Business Planning feature
 */

/**
 * Calculate Projected Effective GCI
 * Formula: targetNetIncome + (targetNetIncome * brokerageSplitPct / (100 - brokerageSplitPct))
 * The result is capped at splitCap if applicable
 *
 * @param targetNetIncome - User's target annual net commission income
 * @param brokerageAnniversary - The brokerage anniversary settings for the forecast year
 * @returns Projected Effective GCI
 */
export function calculateProjectedEffectiveGci(
  targetNetIncome: number,
  brokerageAnniversary: IBrokerageAnniversaryModel | null
): number {
  if (!brokerageAnniversary || !brokerageAnniversary.brokerageSplit) {
    // No brokerage split configured, return target as-is
    return targetNetIncome;
  }

  const brokerageSplitPct = brokerageAnniversary.brokerageSplit;
  const splitCap = brokerageAnniversary.splitCap;
  const additionalChargePct =
    brokerageAnniversary.additionalChargePercentage || 0;
  const additionalChargeCap = brokerageAnniversary.additionalChargeCap;

  // Calculate brokerage portion
  // brokerageAmount = targetNetIncome * brokerageSplitPct / (100 - brokerageSplitPct)
  let brokerageAmount = 0;
  if (brokerageSplitPct < 100) {
    brokerageAmount =
      (targetNetIncome * brokerageSplitPct) / (100 - brokerageSplitPct);
  }

  // Apply cap if set
  if (
    splitCap !== undefined &&
    splitCap !== null &&
    brokerageAmount > splitCap
  ) {
    brokerageAmount = splitCap;
  }

  // Calculate additional charges
  let additionalChargeAmount = 0;
  if (additionalChargePct > 0) {
    additionalChargeAmount =
      (targetNetIncome + brokerageAmount) * (additionalChargePct / 100);
    if (
      additionalChargeCap !== undefined &&
      additionalChargeCap !== null &&
      additionalChargeAmount > additionalChargeCap
    ) {
      additionalChargeAmount = additionalChargeCap;
    }
  }

  return targetNetIncome + brokerageAmount + additionalChargeAmount;
}

/**
 * Calculate Projected GCI
 * Formula: projectedEffectiveGci * (totalGci / totalEffectiveGci)
 *
 * @param projectedEffectiveGci - The calculated projected effective GCI
 * @param totalGci - Total GCI from selected evaluation years
 * @param totalEffectiveGci - Total Effective GCI from selected evaluation years
 * @returns Projected GCI
 */
export function calculateProjectedGci(
  projectedEffectiveGci: number,
  totalGci: number,
  totalEffectiveGci: number
): number {
  if (totalEffectiveGci === 0) {
    return projectedEffectiveGci;
  }
  return projectedEffectiveGci * (totalGci / totalEffectiveGci);
}

/**
 * Calculate GCI percentage for each category item
 * Items below threshold are grouped into "Other"
 *
 * @param items - Array of category items
 * @param totalGci - Total GCI for the category
 * @param threshold - Percentage threshold (default 5%)
 * @returns Items with GCI percentages calculated, small items grouped into "Other"
 */
export function groupSmallContributors(
  items: IBusinessPlanningCategoryItem[],
  totalGci: number,
  threshold: number = 5
): IBusinessPlanningCategoryItem[] {
  if (totalGci === 0 || items.length === 0) {
    return items;
  }

  // Calculate percentage for each item
  const itemsWithPercentage = items.map((item) => ({
    ...item,
    gciPercentage: ((item.totalGci || 0) / totalGci) * 100,
  }));

  // Separate items above and below threshold
  const significantItems = itemsWithPercentage.filter(
    (item) => (item.gciPercentage || 0) >= threshold
  );
  const smallItems = itemsWithPercentage.filter(
    (item) => (item.gciPercentage || 0) < threshold
  );

  // If no small items, return as-is
  if (smallItems.length === 0) {
    return significantItems;
  }

  // Aggregate small items into "Other"
  const otherItem: IBusinessPlanningCategoryItem = {
    id: "other",
    name: "Other",
    totalGci: smallItems.reduce((sum, item) => sum + (item.totalGci || 0), 0),
    totalEffectiveGci: smallItems.reduce(
      (sum, item) => sum + (item.totalEffectiveGci || 0),
      0
    ),
    totalCount: smallItems.reduce(
      (sum, item) => sum + (item.totalCount || 0),
      0
    ),
    totalPrice: smallItems.reduce(
      (sum, item) => sum + (item.totalPrice || 0),
      0
    ),
    avgPrice:
      smallItems.reduce(
        (sum, item) => sum + (item.avgPrice || 0) * (item.totalCount || 0),
        0
      ) /
      Math.max(
        smallItems.reduce((sum, item) => sum + (item.totalCount || 0), 0),
        1
      ),
    avgCommissionPct:
      smallItems.reduce(
        (sum, item) =>
          sum + (item.avgCommissionPct || 0) * (item.totalCount || 0),
        0
      ) /
      Math.max(
        smallItems.reduce((sum, item) => sum + (item.totalCount || 0), 0),
        1
      ),
    gciPercentage: smallItems.reduce(
      (sum, item) => sum + (item.gciPercentage || 0),
      0
    ),
  };

  return [...significantItems, otherItem];
}

/**
 * Calculate transactions needed for a given category item
 * Formula: (categoryGciPct / 100) * projectedGci / (avgPrice * avgCommissionPct / 100)
 *
 * @param gciPercentage - GCI percentage for this category item
 * @param projectedGci - Total projected GCI
 * @param avgPrice - Average price for this category
 * @param avgCommissionPct - Average commission percentage for this category
 * @returns Number of transactions needed (can be decimal)
 */
export function calculateTransactionsNeeded(
  gciPercentage: number,
  projectedGci: number,
  avgPrice: number,
  avgCommissionPct: number
): number {
  if (avgPrice === 0 || avgCommissionPct === 0) {
    return 0;
  }

  const targetGciForCategory = (gciPercentage / 100) * projectedGci;
  const avgGciPerTransaction = avgPrice * (avgCommissionPct / 100);

  return targetGciForCategory / avgGciPerTransaction;
}

/**
 * Generate category projections with all calculations
 *
 * When YTD data is provided ("This Year" forecast), each projection gets a
 * transactionsCompleted count. "Other" completed includes every YTD
 * sub-category that is not shown on its own row: historical small contributors
 * AND sub-categories that are new in the current year (regardless of share).
 *
 * @param categoryData - Raw category data from API
 * @param projectedGci - Calculated projected GCI
 * @param ytdData - Year-to-date transaction data (optional, for "This Year")
 * @param threshold - Percentage threshold for "Other" grouping
 * @returns Category projections with all calculations
 */
export function generateCategoryProjections(
  categoryData: IBusinessPlanningCategoryData,
  projectedGci: number,
  ytdData?: IBusinessPlanningYtdData,
  threshold: number = 5
): IBusinessPlanningCategoryProjection {
  const groupedItems = groupSmallContributors(
    categoryData.items || [],
    categoryData.totalGci || 0,
    threshold
  );

  const projections: IBusinessPlanningProjection[] = groupedItems.map(
    (item) => {
      const transactionsNeeded = calculateTransactionsNeeded(
        item.gciPercentage || 0,
        projectedGci,
        item.avgPrice!,
        item.avgCommissionPct!
      );

      // Find YTD data for this item ("Other" is handled below)
      let transactionsCompleted: number | undefined;
      if (ytdData && ytdData.items && item.id !== "other") {
        const ytdItem = ytdData.items.find(
          (y) => String(y.id) === String(item.id)
        );
        transactionsCompleted = ytdItem?.transactionCount || 0;
      }

      return {
        id: item.id,
        name: item.name,
        gciPercentage: item.gciPercentage || 0,
        avgPrice: item.avgPrice,
        avgCommissionPct: item.avgCommissionPct,
        transactionsNeeded,
        transactionsCompleted,
      };
    }
  );

  // Handle "Other" YTD calculations: small historical contributors plus any
  // sub-categories that are new in the current year
  if (ytdData && ytdData.items) {
    const significantIds = new Set(
      projections.filter((p) => p.id !== "other").map((p) => String(p.id))
    );
    const otherYtdItems = ytdData.items.filter(
      (y) => !significantIds.has(String(y.id || ""))
    );
    const otherYtdCount = otherYtdItems.reduce(
      (sum, y) => sum + (y.transactionCount || 0),
      0
    );

    let otherProjection = projections.find((p) => p.id === "other");
    if (!otherProjection && otherYtdItems.length > 0) {
      // No historical small contributors, but new current-year sub-categories exist
      otherProjection = {
        id: "other",
        name: "Other",
        gciPercentage: 0,
        transactionsNeeded: 0,
      };
      projections.push(otherProjection);
    }

    if (otherProjection) {
      otherProjection.transactionsCompleted = otherYtdCount;
    }
  }

  return {
    category: categoryData.category,
    projections,
  };
}

/**
 * Generate monthly projected GCI trend data
 * Formula: (cumulativeGciToDate / totalGci) * projectedGci
 *
 * @param monthlyTrend - Monthly cumulative GCI data from evaluation years
 * @param totalGci - Total GCI from evaluation years
 * @param projectedGci - Calculated projected GCI
 * @returns Monthly projected GCI values
 */
export function generateProjectedGciTrend(
  monthlyTrend: { month?: number; cumulativeGci?: number }[],
  totalGci: number,
  projectedGci: number
): { month: number; projectedGci: number }[] {
  if (totalGci === 0) {
    return monthlyTrend.map((m) => ({ month: m.month || 0, projectedGci: 0 }));
  }

  return monthlyTrend.map((m) => ({
    month: m.month || 0,
    projectedGci: ((m.cumulativeGci || 0) / totalGci) * projectedGci,
  }));
}

/**
 * Format percentage for display
 */
export function formatPercentage(value: number, decimals: number = 1): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format transaction count for display (1 decimal place)
 */
export function formatTransactionCount(value: number): string {
  return value.toFixed(1);
}

/**
 * Get month name from month number (1-12)
 */
export function getMonthName(month: number): string {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return months[month - 1] || "";
}

/**
 * Get short month name from month number (1-12)
 */
export function getShortMonthName(month: number): string {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return months[month - 1] || "";
}
